import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  ServerIcon,
  ServerStackIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';

export default function SystemMonitor() {
  const { user } = useAuth();
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Mock system data - replace with real API call
  useEffect(() => {
    const mockSystemData = {
      system: {
        cpu_usage: 67,
        memory_usage: 78,
        disk_usage: 45,
        network_bandwidth: 23,
        uptime: '15 days, 8 hours, 32 minutes',
        status: 'healthy'
      },
      video_processing: {
        total_videos: 156,
        processing: 12,
        queued: 8,
        completed: 136,
        failed: 2,
        average_processing_time: '4.5 minutes'
      },
      storage: {
        total_space: '2.5 TB',
        used_space: '1.1 TB',
        available_space: '1.4 TB',
        video_storage: '850 GB',
        transcript_storage: '250 GB'
      },
      performance: {
        response_time: '120ms',
        requests_per_minute: 245,
        error_rate: 0.8,
        active_users: 23
      },
      alerts: [
        {
          id: 1,
          type: 'warning',
          message: 'CPU usage above 60% for 10 minutes',
          timestamp: '2024-01-15T14:30:00Z',
          severity: 'medium'
        },
        {
          id: 2,
          type: 'info',
          message: 'Video processing queue is growing',
          timestamp: '2024-01-15T14:25:00Z',
          severity: 'low'
        }
      ]
    };
    
    setTimeout(() => {
      setSystemStats(mockSystemData);
      setLastUpdated(new Date());
      setLoading(false);
    }, 1000);
  }, []);

  if (!user) {
    return <LoginForm />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading system status...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage < 50) return 'text-green-600';
    if (percentage < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                System Monitor
              </h1>
              <p className="text-lg text-gray-600">
                Real-time system status and performance metrics
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Last updated</div>
              <div className="text-lg font-medium text-gray-900">
                {lastUpdated?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* System Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CpuChipIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">CPU Usage</p>
                <p className={`text-2xl font-bold ${getUsageColor(systemStats.system.cpu_usage)}`}>
                  {systemStats.system.cpu_usage}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ServerIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Memory Usage</p>
                <p className={`text-2xl font-bold ${getUsageColor(systemStats.system.memory_usage)}`}>
                  {systemStats.system.memory_usage}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ServerStackIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Videos Processing</p>
                <p className="text-2xl font-bold text-blue-600">
                  {systemStats.video_processing.processing}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">System Status</p>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(systemStats.system.status)}`}>
                  {systemStats.system.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Video Processing Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <WifiIcon className="h-6 w-6 mr-2 text-purple-600" />
            Video Processing Status
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {systemStats.video_processing.total_videos}
              </div>
              <div className="text-sm text-gray-600">Total Videos</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-2">
                {systemStats.video_processing.processing}
              </div>
              <div className="text-sm text-gray-600">Currently Processing</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {systemStats.video_processing.completed}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">
                {systemStats.video_processing.failed}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600">Queue:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {systemStats.video_processing.queued} videos waiting
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Avg. Processing Time:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {systemStats.video_processing.average_processing_time}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Storage and Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Storage Usage */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Storage Usage</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Total Space</span>
                  <span className="font-medium">{systemStats.storage.total_space}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${(systemStats.storage.used_space.replace(' GB', '') / systemStats.storage.total_space.replace(' TB', '000')) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Video Storage:</span>
                  <span className="ml-2 font-medium">{systemStats.storage.video_storage}</span>
                </div>
                <div>
                  <span className="text-gray-600">Transcript Storage:</span>
                  <span className="ml-2 font-medium">{systemStats.storage.transcript_storage}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Performance Metrics</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Response Time</span>
                <span className="font-medium text-green-600">{systemStats.performance.response_time}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Requests/min</span>
                <span className="font-medium text-blue-600">{systemStats.performance.requests_per_minute}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Error Rate</span>
                <span className="font-medium text-red-600">{systemStats.performance.error_rate}%</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Active Users</span>
                <span className="font-medium text-purple-600">{systemStats.performance.active_users}</span>
              </div>
            </div>
          </div>
        </div>

        {/* System Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-yellow-600" />
            System Alerts
          </h3>
          
          {systemStats.alerts.length > 0 ? (
            <div className="space-y-3">
              {systemStats.alerts.map(alert => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    <span className="ml-3 text-sm text-gray-900">{alert.message}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">No active alerts. System is running smoothly.</p>
            </div>
          )}
        </div>

        {/* System Uptime */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">System Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <span className="text-gray-600">Uptime:</span>
              <span className="ml-2 font-medium text-gray-900">{systemStats.system.uptime}</span>
            </div>
            
            <div>
              <span className="text-gray-600">Network Bandwidth:</span>
              <span className="ml-2 font-medium text-gray-900">{systemStats.system.network_bandwidth}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
